/**
 * TenantBrandingProvider — applies tenant-themed CSS custom properties.
 *
 * Sprint TENANT-BRAND-1.
 *
 * Resolves effective palette = previewPalette ?? tenantPalette (only the
 * admin settings page ever sets previewPalette; everyone else just sees
 * the saved tenant palette). Writes `--brand-*` and `--color-*` inline
 * styles on document.documentElement; clears them when the active mode
 * has no override so static tokens.css takes over via the cascade.
 *
 * Re-applies on theme toggle (useThemeStore.theme).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { apiFetch } from '../lib/api';
import {
  EMPTY_PALETTE,
  PLATFORM_DEFAULTS,
  deriveBrandText,
  deriveHover,
  deriveLight,
  type Palette,
  type PaletteMode,
  type Mode,
} from '../lib/color';
import { useAuthStore } from '../stores/authStore';
import { useThemeStore } from '../stores/themeStore';

interface LogoVariant {
  url: string;
  storage_key?: string;
  content_type?: string;
}

interface BrandingMeta {
  /** Map of mode → logo. May contain only one of light/dark/both. */
  logos: Partial<Record<Mode, LogoVariant>>;
  company_name: string | null;
}

interface TenantBrandingContext {
  palette: Palette;
  meta: BrandingMeta;
  /** Logo for the active mode, falling back to the opposite mode. */
  logoUrl: string | null;
  /** Set the in-flight preview palette (admin settings page only). null to clear. */
  setPreview: (palette: Palette | null) => void;
  /** Force-reload saved branding from the server. */
  reload: () => Promise<void>;
}

const TOKEN_KEYS: ReadonlyArray<keyof PaletteMode> = [
  'brand_primary',
  'brand_primary_hover',
  'brand_primary_light',
  'color_bg',
  'color_surface',
  'color_border',
  'color_text',
  'color_text_secondary',
];

const TOKEN_TO_CSS_VAR: Record<keyof PaletteMode, string> = {
  brand_primary: '--brand-primary',
  brand_primary_hover: '--brand-primary-hover',
  brand_primary_light: '--brand-primary-light',
  color_bg: '--color-bg',
  color_surface: '--color-surface',
  color_border: '--color-border',
  color_text: '--color-text',
  color_text_secondary: '--color-text-secondary',
};

const EMPTY_META: BrandingMeta = {
  logos: {},
  company_name: null,
};

const Ctx = createContext<TenantBrandingContext>({
  palette: EMPTY_PALETTE,
  meta: EMPTY_META,
  logoUrl: null,
  setPreview: () => {},
  reload: async () => {},
});

const HEX6 = /^#[0-9a-fA-F]{6}$/;

function applyPaletteToRoot(palette: Palette, mode: Mode): void {
  const root = document.documentElement;
  const sub = palette[mode];
  const primary = sub.brand_primary && HEX6.test(sub.brand_primary) ? sub.brand_primary : null;
  for (const token of TOKEN_KEYS) {
    const cssVar = TOKEN_TO_CSS_VAR[token];
    let value = sub[token];
    // Auto-derive hover + light only when primary is a valid hex6.
    if (!value && primary) {
      try {
        if (token === 'brand_primary_hover') value = deriveHover(primary, mode);
        else if (token === 'brand_primary_light') value = deriveLight(primary, mode);
      } catch {
        value = null;
      }
    }
    // Only apply hex6 / rgba() / known-good strings; skip mid-typing junk.
    if (value && (HEX6.test(value) || value.startsWith('rgb'))) {
      root.style.setProperty(cssVar, value);
    } else {
      root.style.removeProperty(cssVar);
    }
  }
  // UI-CONTRAST-BRAND — text follows the tenant's brand and surfaces.
  // Without overrides the tokens.css defaults (the same derivation) apply.
  const customised = TOKEN_KEYS.some((token) => sub[token]);
  let derived: ReturnType<typeof deriveBrandText> | null = null;
  try {
    derived = customised ? deriveBrandText(palette, mode) : null;
  } catch {
    derived = null; // mid-edit junk: keep the defaults
  }
  for (const [cssVar, value] of [['--brand-on-primary', derived?.onPrimary], ['--brand-primary-text', derived?.primaryText]] as const) {
    if (value) root.style.setProperty(cssVar, value);
    else root.style.removeProperty(cssVar);
  }
}

function clearAllTokens(): void {
  const root = document.documentElement;
  for (const token of TOKEN_KEYS) {
    root.style.removeProperty(TOKEN_TO_CSS_VAR[token]);
  }
  root.style.removeProperty('--brand-on-primary');
  root.style.removeProperty('--brand-primary-text');
}

interface BrandingApiResponse {
  data: {
    branding: {
      palette?: Palette;
      logos?: Partial<Record<Mode, LogoVariant>>;
      // Legacy pre-TENANT-BRAND-1 flat fields. Folded into logos.light
      // if present and no `logos` key exists.
      logo_url?: string | null;
      logo_storage_key?: string | null;
      logo_content_type?: string | null;
      company_name?: string | null;
      primary_color?: string | null;
    };
  };
}

function paletteFromBranding(branding: BrandingApiResponse['data']['branding']): Palette {
  if (branding.palette && (branding.palette.light || branding.palette.dark)) {
    return {
      light: { ...EMPTY_PALETTE.light, ...(branding.palette.light ?? {}) },
      dark: { ...EMPTY_PALETTE.dark, ...(branding.palette.dark ?? {}) },
    };
  }
  // Fall back to legacy primary_color → light only.
  if (branding.primary_color) {
    return {
      light: { ...EMPTY_PALETTE.light, brand_primary: branding.primary_color },
      dark: { ...EMPTY_PALETTE.dark },
    };
  }
  return { light: { ...EMPTY_PALETTE.light }, dark: { ...EMPTY_PALETTE.dark } };
}

function metaFromBranding(branding: BrandingApiResponse['data']['branding']): BrandingMeta {
  const logos: Partial<Record<Mode, LogoVariant>> = {};
  if (branding.logos) {
    if (branding.logos.light?.url) logos.light = branding.logos.light;
    if (branding.logos.dark?.url) logos.dark = branding.logos.dark;
  }
  // Legacy fallback — pre-sprint single-logo data, surface as light.
  if (!logos.light && branding.logo_url) {
    logos.light = {
      url: branding.logo_url,
      storage_key: branding.logo_storage_key ?? undefined,
      content_type: branding.logo_content_type ?? undefined,
    };
  }
  return {
    logos,
    company_name: branding.company_name ?? null,
  };
}

interface Props {
  children: ReactNode;
}

export default function TenantBrandingProvider({ children }: Props) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const tenantId = useAuthStore((s) => s.user?.tenantId);
  const theme = useThemeStore((s) => s.theme);

  const [tenantPalette, setTenantPalette] = useState<Palette>(EMPTY_PALETTE);
  const [meta, setMeta] = useState<BrandingMeta>(EMPTY_META);
  const [previewPalette, setPreviewPalette] = useState<Palette | null>(null);
  const lastTenantIdRef = useRef<string | null>(null);

  const reload = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await apiFetch<BrandingApiResponse>('/tenants/me/branding');
      const branding = res.data.branding || {};
      setTenantPalette(paletteFromBranding(branding));
      setMeta(metaFromBranding(branding));
    } catch {
      // Quietly fall back to platform defaults — provider must never
      // crash app boot.
      setTenantPalette(EMPTY_PALETTE);
      setMeta(EMPTY_META);
    }
  }, [isAuthenticated]);

  // Load on auth / tenant switch.
  useEffect(() => {
    if (!isAuthenticated || !tenantId) {
      clearAllTokens();
      setTenantPalette(EMPTY_PALETTE);
      setMeta(EMPTY_META);
      setPreviewPalette(null);
      lastTenantIdRef.current = null;
      return;
    }
    if (tenantId !== lastTenantIdRef.current) {
      lastTenantIdRef.current = tenantId;
      setPreviewPalette(null);
      void reload();
    }
  }, [isAuthenticated, tenantId, reload]);

  // Re-apply on every palette / preview / theme change.
  const effective = previewPalette ?? tenantPalette;
  useEffect(() => {
    applyPaletteToRoot(effective, theme);
  }, [effective, theme]);

  const logoUrl = useMemo<string | null>(() => {
    const current = meta.logos[theme]?.url;
    if (current) return current;
    const otherMode: Mode = theme === 'light' ? 'dark' : 'light';
    return meta.logos[otherMode]?.url ?? null;
  }, [meta.logos, theme]);

  const ctxValue = useMemo<TenantBrandingContext>(
    () => ({
      palette: effective,
      meta,
      logoUrl,
      setPreview: setPreviewPalette,
      reload,
    }),
    [effective, meta, logoUrl, reload],
  );

  return <Ctx.Provider value={ctxValue}>{children}</Ctx.Provider>;
}

export function useTenantBranding(): TenantBrandingContext {
  return useContext(Ctx);
}

export { PLATFORM_DEFAULTS };
